(() => {
  const CPB_DEBUG = true;
  const cpbLog = (...args) => CPB_DEBUG && console.log('[CPB]', ...args);
  const cpbWarn = (...args) => CPB_DEBUG && console.warn('[CPB]', ...args);
  const cpbError = (...args) => CPB_DEBUG && console.error('[CPB]', ...args);

  cpbLog('script loaded', { href: window.location.href, readyState: document.readyState });

  let cartCache = null;
  let removing = false;

  primeCartCache();

  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.cartData?.items) {
        cartCache = event.cartData;
        cpbLog('cart cache updated from cartUpdate event', { item_count: cartCache.item_count });
      } else {
        primeCartCache();
      }
    });
  }

  document.documentElement.addEventListener('cart:updated', (event) => {
    if (event?.detail?.cart?.items) {
      cartCache = event.detail.cart;
      cpbLog('cart cache updated from cart:updated event', { item_count: cartCache.item_count });
    }
  });

  async function primeCartCache() {
    try {
      cartCache = await getCart();
      cpbLog('initial cart snapshot', {
        item_count: cartCache.item_count,
        items: cartCache.items.map((item) => ({
          key: item.key,
          product_type: item.product_type,
          frsId: getFrsId(item),
          properties: item.properties
        }))
      });
    } catch (err) {
      cpbError('could not prime cart cache', err);
    }
  }

  document.addEventListener('click', onRemoveClick, true);

  async function onRemoveClick(e) {
    const hit = e.target.closest?.('line-item__remove-button-cpb, .line-item__remove-button-cpb');
    if (!hit) return;

    e.preventDefault();
    e.stopImmediatePropagation();

    if (removing) return; // защита от двойного клика
    const removeBtn = hit.closest('line-item__remove-button-cpb') || hit;
    cpbLog('remove button clicked', removeBtn);

    removing = true;
    try {
      await removeGroup(removeBtn);
    } catch (err) {
      cpbError('remove failed', err);
    }

    window.location.assign(cartUrl());
  }

  async function removeGroup(removeBtn) {
    let cart = await getCart();
    cartCache = cart;

    let keys = computeGroupedKeys(removeBtn, cart);
    if (!keys.length) {
      cpbWarn('could not resolve clicked cart item');
      return;
    }

    try {
      const updated = await removeCartItems(keys);
      cartCache = updated;
      cpbLog('removed group (atomic)', { keys, item_count: updated?.item_count });
      return;
    } catch (err) {
      cpbWarn('update failed, refetch + retry with valid keys', err);
    }

    cart = await getCart();
    cartCache = cart;
    const valid = new Set(cart.items.map((item) => item.key));
    keys = keys.filter((key) => valid.has(key));
    if (!keys.length) {
      cpbLog('nothing left to remove after refetch');
      return;
    }

    const updated = await removeCartItems(keys);
    cartCache = updated;
    cpbLog('removed group after retry', { keys, item_count: updated?.item_count });
  }

  async function removeCartItems(keys) {
    const updates = {};
    for (const key of keys) {
      if (key) updates[key] = 0;
    }
    if (!Object.keys(updates).length) {
      throw new Error('removeCartItems: no valid keys');
    }

    const root = window.Shopify?.routes?.root || '/';
    cpbLog('removeCartItems request', { updates });

    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
      credentials: 'same-origin',
      cache: 'no-store',
      keepalive: true
    });

    if (!response.ok) {
      const body = await response.text().catch(() => '');
      cpbError('cart/update.js rejected', { status: response.status, body });
      throw new Error(`cart/update.js failed: ${response.status}`);
    }
    return response.json();
  }

  function cartUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart`;
  }

  function computeGroupedKeys(removeBtn, cart) {
    const line = getLineIndex(removeBtn);
    const clickedItem = getClickedCartItem(removeBtn, cart, line);
    if (!clickedItem?.key) {
      cpbWarn('could not resolve clicked cart item', { line });
      return [];
    }

    const frsId = getFrsId(clickedItem);
    cpbLog('clicked line resolved', {
      line,
      clickedKey: clickedItem.key,
      frsId,
      pairs: cart.items.map((item) => ({ key: item.key, frsId: getFrsId(item) }))
    });

    if (frsId) {
      const grouped = cart.items
        .filter((item) => getFrsId(item) === frsId)
        .map((item) => item.key)
        .filter(Boolean);
      cpbLog('bundle keys to remove', { frsId, keys: grouped });
      if (grouped.length > 1) return grouped;
    } else {
      cpbLog('no _cpb_frs_id -> single remove', { key: clickedItem.key });
    }

    return [clickedItem.key];
  }

  function getLineIndex(removeBtn) {
    const href = removeBtn.getAttribute?.('href');
    if (href) {
      try {
        const url = new URL(href, window.location.origin);
        const hrefLine = Number.parseInt(url.searchParams.get('line') ?? '', 10);
        if (Number.isFinite(hrefLine) && hrefLine > 0) return hrefLine;
      } catch {
        cpbWarn('could not parse remove button href', { href });
      }
    }

    const dataIndex = Number.parseInt(removeBtn.getAttribute?.('data-index') ?? '', 10);
    if (Number.isFinite(dataIndex) && dataIndex > 0) return dataIndex;

    const idFromId = Number.parseInt(String(removeBtn.id || '').replace(/\D+/g, ''), 10);
    if (Number.isFinite(idFromId) && idFromId > 0) return idFromId;

    const cartRoot = removeBtn.closest('cart-items, cart-drawer-items') || document;
    const lineItem = removeBtn.closest('.cart-item, .line-item, tr, li');
    if (lineItem) {
      const items = Array.from(cartRoot.querySelectorAll('.cart-item, .line-item'));
      const idx = items.indexOf(lineItem);
      if (idx >= 0) return idx + 1;
    }

    return null;
  }

  function getClickedCartItem(removeBtn, cart, line) {
    const bundleId = normalizeFrsId(
      removeBtn.closest('[data-cpb-custom-bundle]')?.getAttribute('data-cpb-custom-bundle')
    );
    const cartKey = removeBtn.closest('[data-cart-key]')?.getAttribute('data-cart-key');

    if (cartKey) {
      const byKey = cart.items.find((item) => item.key === cartKey);
      if (byKey) return byKey;
    }

    if (bundleId) {
      const byBundle = cart.items.find((item) => getFrsId(item) === bundleId);
      if (byBundle) return byBundle;
    }

    if (line && cart.items?.[line - 1]) {
      return cart.items[line - 1];
    }

    if (bundleId) {
      const normalizedBundleId = normalizeFrsId(bundleId);
      const byLooseBundle = cart.items.find((item) => {
        const itemFrsId = normalizeFrsId(getFrsId(item));
        return itemFrsId && (itemFrsId === normalizedBundleId || itemFrsId.includes(normalizedBundleId) || normalizedBundleId.includes(itemFrsId));
      });
      if (byLooseBundle) return byLooseBundle;
    }

    return line ? cart.items[line - 1] : null;
  }

  function normalizeFrsId(value) {
    if (value == null) return '';
    const str = String(value).trim().replace(/^['"]|['"]$/g, '').trim();
    try {
      return decodeURIComponent(str);
    } catch {
      return str;
    }
  }

  function getFrsId(item) {
    const properties = item?.properties;
    if (!properties || typeof properties !== 'object') return '';

    if (Object.prototype.hasOwnProperty.call(properties, '_cpb_frs_id')) {
      return normalizeFrsId(properties._cpb_frs_id);
    }

    const frsEntry = Object.entries(properties).find(([key]) => {
      const normalizedKey = key?.toLowerCase?.().trim?.();
      return normalizedKey === '_cpb_frs_id' || normalizedKey?.includes('cpb_frs');
    });
    return normalizeFrsId(frsEntry?.[1]);
  }

  async function getCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js?_=${Date.now()}`, {
      credentials: 'same-origin',
      cache: 'no-store'
    });
    if (!response.ok) throw new Error(`cart.js failed: ${response.status}`);
    return response.json();
  }
})();